import React, { useRef, forwardRef, useImperativeHandle } from 'react';

import CourtesyMessage from '../../organisms/courtesy-message/courtesy-message';
import PlayerCard, { IPlayerCard } from '../../molecules/player-card/player-card';
import PlayerCardSlim from '../../molecules/player-card-slim/player-card-slim';
import PlayerCardWrapper from '../../molecules/player-card-wrapper/player-card-wrapper';
import Text from '../../atoms/text/text';
import IconFootballGrey from '../../atoms/icons/icon-football-grey';
import CardAdv from '../../atoms/card-adv/card-adv';
import Cta from '../../atoms/cta/cta';
import { playerCardtypes } from '../../../typings/player';
import { makeClassNames } from '../../../utils/styles';
import t from '../../../labels/labelling-utils';

import { handlePlayerCard } from './player-card-utils';
import './scorers-ranking-player-page.scss';

type playerCard = Omit<IPlayerCard, 'firstName' | 'playerTeamColor'>;
type playerCardRow = Omit<playerCard, 'playerTeamImageUrl' | 'playerTeamShowPoints'>;

export enum observedTypes {
  PLAYER_FULL = 'player-full',
  PLAYER_ROW = 'player-row',
  ADV = 'adv',
}
interface IScorer {
  playerSurname?: string | null;
  teamId?: string;
  playerId?: string;
  seoTeamName: string;
  teamUrl: string;
  teamName?: string | null;
  logoUrl: string;
  position: number;
  goal: number;
  playerPageLink?: string;
  playerImageUrl?: string;
}
export interface IScorersRankingPlayerPageProps {
  competitionId?: string;
  seasonId?: string;
  scorers: IScorer[];
  competitionSlugName: string;
  numberOfHeadShots?: number;
  advsByHeadshots?: number;
  advByScorers?: number;
  competitionName: string;
  competitionCalendarUrl: string;
  cardsColor: string;
  advBoxAttributes?: React.HTMLAttributes<HTMLDivElement> & {
    'data-revivezoneid': string;
    'data-reviveid': string;
  };
  lazyOffset?: number;
  trackingData?: any;
  analyticsElement?: string;
  fakeAdvs?: boolean;
}

const classNames = () =>
  makeClassNames({
    scorersRanking: 'scorers-ranking-player-page',
    title: 'scorers-ranking-player-page__title',
    players: 'scorers-ranking-player-page__players',
    playerWrapper: 'scorers-ranking-player-page__player-wrapper',
    paragraph: ['text', 'text--color-blue', 'text--font-size-16', 'text--weight-400'],
    courtesy: 'scorers-ranking-player-page__courtesy',
    courtesyFooter: 'scorers-ranking-player-page__courtesy-footer',
    courtesyBody: 'scorers-ranking-player-page__courtesy-body',
    playerPrapperCardSlim: 'player-wrapper--card-slim',
  });

const cn = classNames();

const Courtesy = (props: {
  ctaUrl: string;
  ctaLable: string;
  text: string;
  trackingDataValue?: string;
  analyticsElement?: string;
}) => {
  const { ctaUrl, ctaLable, text, trackingDataValue, analyticsElement } = props;

  const iconFootball = () => <IconFootballGrey size="xxlarge" color="medium-light" />;

  return (
    <div className={cn.courtesy} data-analytics-element={analyticsElement || undefined}>
      <div className={cn.courtesyBody}>
        <CourtesyMessage text={text} backgroundIcon={iconFootball()} fontSize="18--22" />
      </div>
      <div className={cn.courtesyFooter}>
        <Cta
          color="red-light"
          fontSize="14"
          fontWeight="500"
          iconPosition="right"
          iconSize="xsmall"
          href={ctaUrl}
          verticalAlign="middle"
          trackingData={trackingDataValue ? { intcmp: trackingDataValue } : undefined}
        >
          {ctaLable}
        </Cta>
      </div>
    </div>
  );
};

const ScorersRankingPlayerPage = forwardRef<unknown, IScorersRankingPlayerPageProps>((props, ref) => {
  const {
    scorers,
    numberOfHeadShots,
    advsByHeadshots,
    advByScorers,
    competitionName,
    competitionCalendarUrl,
    cardsColor,
    advBoxAttributes,
    lazyOffset = 0,
    competitionSlugName,
    trackingData,
    analyticsElement,
    fakeAdvs = false,
  } = props;

  const divRef = useRef<HTMLDivElement | null>(null);
  const fullPlayersRef = useRef<HTMLDivElement[]>([]);
  const rowPlayersRef = useRef<HTMLDivElement[]>([]);
  const rowsHalfCardRef = useRef<number>(0);

  const addToFullPlayersRef = (element: HTMLDivElement) => {
    if (element && !fullPlayersRef?.current?.includes(element)) {
      fullPlayersRef.current.push(element);
    }
  };

  const addToRowPlayersRef = (element: HTMLDivElement) => {
    if (element && !rowPlayersRef?.current?.includes(element)) {
      rowPlayersRef.current.push(element);
    }
  };

  const addToRowsHalfCardRef = (element: HTMLDivElement) => {
    if (element && !rowPlayersRef?.current?.includes(element)) {
      rowPlayersRef.current.push(element);
    }
  };

  useImperativeHandle(ref, () => {
    return {
      fullPlayersRef: fullPlayersRef.current,
      rowPlayersRef: rowPlayersRef.current,
      rowsHalfCardRef: rowsHalfCardRef.current,
      handlePlayerCard,
      removeObserveType: (element: HTMLDivElement) => {
        element?.removeAttribute('data-observe-type');
      },
    };
  });

  if (scorers?.length < 1) {
    return (
      <Courtesy
        ctaUrl={competitionCalendarUrl}
        ctaLable={`${t('calendar.cta')} ${competitionName}`}
        text={t('scorers.ranking.player.page.courtesy.label')}
        trackingDataValue={trackingData?.[`${competitionSlugName}_Calendar`] || undefined}
        analyticsElement={analyticsElement}
      />
    );
  }

  const playerCards = { full: scorers?.slice(0, numberOfHeadShots), rows: scorers?.slice(numberOfHeadShots) };

  const fullPlayers: playerCard[] = playerCards.full?.map((scorer) => ({
    type: playerCardtypes.MARCATORI,
    imageUrl: scorer.playerImageUrl || '',
    lastName: scorer.playerSurname || '',
    playerUrl: scorer.playerPageLink || '',
    playerTeamImageUrl: scorer.logoUrl,
    playerTeamName: scorer.teamName || '',
    playerPoints: scorer.goal,
    playerPosition: scorer.position,
    playerTeamShowPoints: true,
    playerTeamUrl: scorer.teamUrl,
  }));

  const rowsPlayers: playerCard[][] = playerCards.rows?.reduce<playerCard[][]>((array, _, index) => {
    if (index % advByScorers! === 0) {
      array.push(
        playerCards.rows.slice(index, index + advByScorers!).map((scorer) => ({
          type: playerCardtypes.MARCATORI,
          imageUrl: scorer.playerImageUrl || '',
          lastName: scorer.playerSurname || '',
          playerUrl: scorer.playerPageLink || '',
          playerTeamImageUrl: scorer.logoUrl,
          playerTeamName: scorer.teamName || '',
          playerPoints: scorer.goal,
          playerPosition: scorer.position,
          playerTeamShowPoints: true,
          playerTeamUrl: scorer.teamUrl,
        }))
      );
    }
    return array;
  }, []);

  const playersFull: playerCard[] = fullPlayers || [];
  const playersRows: playerCardRow[][] = rowsPlayers || [];

  const type = playerCardtypes.MARCATORI;
  const title = t('scorers.ranking.player.page.title');

  const getHalfCardRef = () => {
    rowsHalfCardRef.current += 1;
    return rowsHalfCardRef.current;
  };

  return (
    <div className={cn.scorersRanking} ref={divRef} data-analytics-element={analyticsElement || undefined}>
      <div className={cn.title}>
        <Text color="blue" fontSize="22" fontWeight="500">
          {title}
        </Text>
      </div>
      <div className={cn.players}>
        {playersFull?.map((player, index) => (
          <div
            key={`player-${index}`}
            className={cn.playerWrapper}
            ref={addToFullPlayersRef}
            data-observe-type={observedTypes.PLAYER_FULL}
            data-analytics-card={index + 1}
          >
            <PlayerCard
              type={type}
              imageUrl={player.imageUrl}
              lastName={player.lastName}
              playerUrl={player.playerUrl}
              playerTeamImageUrl={player.playerTeamImageUrl}
              playerTeamName={player.playerTeamName}
              playerPoints={player.playerPoints}
              playerPosition={player.playerPosition}
              playerTeamShowPoints
              playerTeamUrl={player.playerTeamUrl}
              label="GOL"
              lazy={lazyOffset >= 0 && index >= lazyOffset}
              trackingDataValues={{
                playerCard: trackingData?.playerCard || undefined,
                playerTeam: trackingData?.playerTeam || undefined,
              }}
            />
            {advBoxAttributes && advsByHeadshots && index > 0 && (index + 1) % advsByHeadshots === 0 ? (
              <CardAdv advBoxAttributes={advBoxAttributes} shiftingContent fakeAdvs={fakeAdvs} />
            ) : null}
          </div>
        ))}
        {playersRows?.map((players, index) => (
          <div
            key={`player-wrapper-${index}-${index}`}
            ref={addToRowPlayersRef}
            className={cn.playerWrapper}
            data-observe-type={observedTypes.PLAYER_ROW}
          >
            <PlayerCardWrapper playerTeamColor={cardsColor} hasHeadShot={false}>
              <div className={cn.playerWrapper}>
                {players.map((player, playerIndex) => (
                  <div
                    key={`player-row-${index}-${playerIndex}`}
                    data-analytics-card={playerIndex % 5 === 0 ? getHalfCardRef() + fullPlayers.length : null}
                    ref={addToRowsHalfCardRef}
                    className={cn.playerPrapperCardSlim}
                  >
                    <PlayerCardSlim
                      lastName={player.lastName}
                      imageUrl={player.imageUrl}
                      playerUrl={player.playerUrl}
                      playerPoints={player.playerPoints}
                      playerTeamName={player.playerTeamName}
                      lazy={lazyOffset >= 0}
                      trackingDataValue={trackingData?.playerList || undefined}
                    />
                  </div>
                ))}
              </div>
            </PlayerCardWrapper>
            {advBoxAttributes && index < playersRows.length - 1 ? (
              <CardAdv advBoxAttributes={advBoxAttributes} shiftingContent fakeAdvs={fakeAdvs} />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
});

ScorersRankingPlayerPage.displayName = 'ScorersRankingPlayerPage';

export default ScorersRankingPlayerPage;
